import {Component, Input, OnInit} from '@angular/core';
import {ProductImageService} from '../../services/product-images/product-image.service';
import {ProductImage} from '../../models/product';
import {FaIconComponent} from '@fortawesome/angular-fontawesome';
import {
  faCheck,
  faExclamationTriangle,
  faMagnifyingGlass,
  faTrash,
  faUpload,
  faSpinner,
  faWandMagicSparkles
} from '@fortawesome/free-solid-svg-icons';
import {Observable} from 'rxjs';
import {AsyncPipe} from '@angular/common';
import {UiButtonComponent} from '../../shared/ui/button/ui-button.component';
import {ImageSearchPanelComponent} from '../image-search-panel/image-search-panel.component';
import { AppLogger, AppLoggerService } from '../../core/logging';

@Component({
  selector: 'app-product-image-manager',
  imports: [
    FaIconComponent,
    AsyncPipe,
    UiButtonComponent,
    ImageSearchPanelComponent
  ],
  templateUrl: './product-image-manager.component.html',
  styleUrl: './product-image-manager.component.scss'
})
export class ProductImageManagerComponent implements OnInit {
  private readonly tag = '[ProductImageManagerComponent]';
  private readonly log: AppLogger;

  @Input() productId!: number;

  images: ProductImage[] = [];
  selectedImage: ProductImage | null = null;
  isUploading = false;
  isDraggingOver = false;
  /** Background removal in flight — a remote model call, so a few seconds. */
  isEnhancing = false;
  enhanceError = '';
  /** When true the search panel replaces the upload/grid column. */
  isSearchOpen = false;

  faCheck = faCheck;
  faExclamation = faExclamationTriangle;
  faTrash = faTrash;
  faUpload = faUpload;
  faSpinner = faSpinner;
  faSearch = faMagnifyingGlass;
  faWand = faWandMagicSparkles;

  get fullImages(): ProductImage[] {
    return this.images.filter(img => img.type === 'full');
  }

  get thumbnailImages(): ProductImage[] {
    return this.images.filter(img => img.type === 'thumbnail');
  }

  get hasPrimaryFull(): boolean {
    return this.fullImages.some(img => img.is_primary);
  }

  get hasPrimaryThumbnail(): boolean {
    return this.thumbnailImages.some(img => img.is_primary);
  }

  constructor(
    private productImageService: ProductImageService,
    private appLogger: AppLoggerService
  ) {
    this.log = this.appLogger.createForClass(ProductImageManagerComponent);
  }

  ngOnInit(): void {
    if (this.productId) {
      this.loadImages();
    }
  }

  private loadImages(): void {
    this.productImageService.getImages(this.productId).subscribe({
      next: (images: ProductImage[]) => {
        this.images = images;
        // Select the primary full image by default, fallback to primary thumbnail, then first image
        const primaryFull = this.images.find(img => img.type === 'full' && img.is_primary);
        const primaryThumbnail = this.images.find(img => img.type === 'thumbnail' && img.is_primary);

        if (primaryFull) {
          this.selectedImage = primaryFull;
        } else if (primaryThumbnail) {
          this.selectedImage = primaryThumbnail;
        } else if (this.images.length > 0) {
          this.selectedImage = this.images[0];
        }
        this.log.debug('Loaded images for product', { productId: this.productId, images: this.images });
      },
      error: (err) => {
        this.log.error('Failed to load images for product', err, { productId: this.productId });
        this.images = [];
      }
    });
  }

  private loadImagesAndRestoreSelection(imageIdToRestore: number): void {
    this.productImageService.getImages(this.productId).subscribe({
      next: (images: ProductImage[]) => {
        this.images = images;
        // Try to restore the previously selected image
        const restoredImage = this.images.find(img => img.id === imageIdToRestore);
        if (restoredImage) {
          this.selectedImage = restoredImage;
        } else {
          // Fallback to primary full image if the restored image is not found
          const primaryFull = this.images.find(img => img.type === 'full' && img.is_primary);
          const primaryThumbnail = this.images.find(img => img.type === 'thumbnail' && img.is_primary);

          if (primaryFull) {
            this.selectedImage = primaryFull;
          } else if (primaryThumbnail) {
            this.selectedImage = primaryThumbnail;
          } else if (this.images.length > 0) {
            this.selectedImage = this.images[0];
          }
        }
        this.log.debug('Loaded images and restored selection', { productId: this.productId, imageIdToRestore, images: this.images });
      },
      error: (err) => {
        this.log.error('Failed to load images for product', err, { productId: this.productId });
        this.images = [];
      }
    });
  }

  selectImage(image: ProductImage): void {
    this.selectedImage = image;
  }

  openSearch(): void {
    this.log.debug('Opening image search', { productId: this.productId });
    this.isSearchOpen = true;
  }

  closeSearch(): void {
    this.isSearchOpen = false;
  }

  /**
   * An imported image is stored with all its variants, so the whole list is
   * reloaded. The panel stays open so several images can be picked in one go.
   */
  onImageImported(): void {
    this.log.debug('Searched image imported', { productId: this.productId });
    this.loadImages();
  }

  /** Credit line for an image that came from an image search. */
  attributionFor(image: ProductImage | null): string {
    const attribution = image?.attribution;
    if (!attribution) {
      return '';
    }
    const parts = [attribution.provider];
    if (attribution.author) {
      parts.push(attribution.author);
    }
    if (attribution.license) {
      parts.push(attribution.license);
    }
    return parts.filter(Boolean).join(' · ');
  }

  makePrimary(): void {
    const imageToMakePrimary = this.selectedImage || this.images[0];
    if (imageToMakePrimary?.id && !imageToMakePrimary.is_primary) {
      this.log.debug('Making image primary', { imageToMakePrimary });
      const selectedImageId = imageToMakePrimary.id;
      const imageType = imageToMakePrimary.type;

      // Optimistically update the UI to prevent flickering
      this.images = this.images.map(img => {
        if (img.type === imageType) {
          // Remove primary from all images of the same type
          return { ...img, is_primary: img.id === selectedImageId };
        }
        return img;
      });

      // Update the selected image reference
      this.selectedImage = this.images.find(img => img.id === selectedImageId) || null;

      this.productImageService.setAsPrimaryImage(selectedImageId).subscribe({
        next: () => {
          this.log.debug('Image set as primary successfully');
          // Reload in background to sync with server state
          this.loadImagesAndRestoreSelection(selectedImageId);
        },
        error: (err) => {
          this.log.error('Failed to set image as primary', err, { selectedImageId });
          // Rollback optimistic update on error
          this.loadImagesAndRestoreSelection(selectedImageId);
        }
      });
    }
  }

  /**
   * Whether this image can be run through background removal. Only images we
   * hold the bytes for qualify — provider images are bare hotlinks, so the
   * backend has nothing to send to the model.
   */
  canEnhance(image: ProductImage | null): boolean {
    return this.isUserManaged(image);
  }

  /**
   * Cut the product out of its background. The result is stored as a new photo
   * and becomes the primary, so the list reloads and selects it; the source
   * photo stays put and can be re-selected if the cut-out is unconvincing.
   */
  enhanceImage(): void {
    const image = this.selectedImage || this.images[0];
    if (!image?.id || this.isEnhancing || !this.canEnhance(image)) {
      return;
    }

    const knownIds = new Set(this.images.map(img => img.id));

    this.isEnhancing = true;
    this.enhanceError = '';
    this.log.debug('Enhancing image', { imageId: image.id, productId: this.productId });

    this.productImageService.enhanceImage(image.id).subscribe({
      next: () => {
        this.log.debug('Image enhanced successfully', { imageId: image.id });
        this.isEnhancing = false;
        this.productImageService.getImages(this.productId).subscribe({
          next: (images: ProductImage[]) => {
            this.images = images;
            // Show the result: the newest 'full' row we hadn't seen before.
            const created = images.filter(img => img.type === 'full' && !knownIds.has(img.id));
            this.selectedImage =
              created[created.length - 1]
              ?? images.find(img => img.type === 'full' && img.is_primary)
              ?? this.selectedImage;
          },
          error: (err) => {
            this.log.error('Failed to reload images after enhance', err, { productId: this.productId });
          }
        });
      },
      error: (err) => {
        this.log.error('Failed to enhance image', err, { imageId: image.id });
        this.isEnhancing = false;
        this.enhanceError = err?.status === 503
          ? 'Background removal is not configured on the server.'
          : 'Could not remove the background. Please try again.';
      }
    });
  }

  deleteImage(): void {
    const imageToDelete = this.selectedImage || this.images[0];
    if (imageToDelete?.id) {
      this.log.debug('Deleting image', { imageToDelete });
      this.productImageService.deleteImage(imageToDelete.id).subscribe({
        next: () => {
          this.log.debug('Image deleted successfully');
          this.selectedImage = null;
          this.loadImages();
        },
        error: (err) => {
          this.log.error('Failed to delete image', err, { imageId: imageToDelete.id });
        }
      });
    }
  }

  uploadImage(): void {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/png,image/jpeg';

    input.onchange = (event: any) => {
      const file = event.target.files[0];
      if (file) {
        this.handleFileUpload(file);
      }
    };

    input.click();
  }

  private handleFileUpload(file: File): void {
    if (!file.type.match(/image\/(png|jpeg)/)) {
      this.log.warn('Invalid file type for image upload', { fileType: file.type });
      return;
    }

    this.isUploading = true;
    this.log.debug('Uploading image', { fileName: file.name, productId: this.productId });

    this.productImageService.uploadImage(this.productId, file, true).subscribe({
      next: (response) => {
        this.log.debug('Image uploaded successfully', { response });
        this.isUploading = false;
        this.loadImages();
      },
      error: (err) => {
        this.log.error('Failed to upload image', err, { productId: this.productId, fileName: file.name });
        this.isUploading = false;
      }
    });
  }

  getProviderLogoPath(provider: string | undefined): Observable<String> {
    if (!provider) return new Observable(observer => {
      observer.next('assets/img/provider-logos/unknown.png');
      observer.complete();
    })

    return new Observable(observer => {
      observer.next('assets/img/provider-logos/' +
        provider.toLowerCase().replace(/ /g, '-') +
        '.png');
      observer.complete();
    })
  }

  isUserOwned(image: ProductImage | null): boolean {
    return image?.owner_type?.toLowerCase() === 'user';
  }

  /** Images the user picked from an image search — deletable like uploads. */
  isSearchOwned(image: ProductImage | null): boolean {
    return image?.owner_type?.toLowerCase() === 'search';
  }

  /** Whether the user may delete this image (their upload or their pick). */
  isUserManaged(image: ProductImage | null): boolean {
    return this.isUserOwned(image) || this.isSearchOwned(image);
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingOver = true;
    this.log.debug('Drag over detected');
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingOver = false;
    this.log.debug('Drag leave detected');
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingOver = false;

    this.log.debug('Drop event detected', { dataTransfer: event.dataTransfer ? {
      types: Array.from(event.dataTransfer.types ?? []),
      dropEffect: event.dataTransfer.dropEffect,
      effectAllowed: event.dataTransfer.effectAllowed
    } : undefined });

    if (this.isUploading) {
      return;
    }

    // Try to get files first (for local file drops)
    const files = event.dataTransfer?.files;
    this.log.debug('Drop files detected', { fileCount: files?.length ?? 0 });
    if (files && files.length > 0) {
      const file = files[0];
      this.log.debug('Handling file drop', { fileName: file.name, fileType: file.type, fileSize: file.size });
      this.handleFileUpload(file);
      return;
    }

    // Try to get image URL (for images dragged from web pages)
    const imageUrl = event.dataTransfer?.getData('text/html');
    this.log.debug('Drop HTML data detected', { hasHtml: !!imageUrl, htmlLength: imageUrl?.length ?? 0 });
    if (imageUrl) {
      // Extract img src from HTML
      const imgMatch = imageUrl.match(/<img[^>]+src="([^">]+)"/);
      if (imgMatch && imgMatch[1]) {
        this.log.debug('Extracted image URL from HTML', { url: imgMatch[1] });
        this.handleImageUrlDrop(imgMatch[1]);
        return;
      }
    }

    // Try direct URL
    const url = event.dataTransfer?.getData('text/uri-list') || event.dataTransfer?.getData('text/plain');
    this.log.debug('Drop URL data detected', { url });
    if (url && (url.startsWith('http://') || url.startsWith('https://'))) {
      this.log.debug('Handling URL drop', { url });
      this.handleImageUrlDrop(url);
    } else {
      this.log.warn('No valid drop data found', { url });
    }
  }

  private handleImageUrlDrop(imageUrl: string): void {
    this.log.debug('Downloading image from URL', { url: imageUrl });
    this.isUploading = true;

    // Fetch the image from the URL
    fetch(imageUrl)
      .then(response => {
        if (!response.ok) {
          throw new Error('Failed to fetch image');
        }
        return response.blob();
      })
      .then(blob => {
        // Convert blob to File
        const fileName = this.getFileNameFromUrl(imageUrl);
        const file = new File([blob], fileName, { type: blob.type });
        this.handleFileUpload(file);
      })
      .catch(err => {
        this.log.error('Failed to download image from URL', err);
        this.isUploading = false;
        alert('Failed to download image. This might be due to CORS restrictions. Try downloading the image first and then uploading it.');
      });
  }

  private getFileNameFromUrl(url: string): string {
    try {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;
      const fileName = pathname.substring(pathname.lastIndexOf('/') + 1);
      return fileName || 'downloaded-image.jpg';
    } catch {
      return 'downloaded-image.jpg';
    }
  }
}
